<template>
  <v-container>
    <v-row class="mb-6">
      <v-col cols="12" sm="6">
        <h1>Gigs</h1>
      </v-col>
      <v-col v-if="orgStore.isManagerPlus" cols="12" sm="6" class="text-right">
        <v-btn color="primary" @click="showCreateDialog = true">
          <v-icon start>mdi-plus</v-icon>
          New Gig
        </v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" closable @click:close="error = null">
      {{ error }}
    </v-alert>

    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="tableItems"
          :loading="isLoading"
          v-model:page="page"
          v-model:items-per-page="itemsPerPage"
          v-model:sort-by="sortBy"
          item-value="id"
          @click:row="onRowClick"
        >
          <template #item.name="{ item }">
            <router-link :to="`/gigs/${item.id}`" class="text-primary text-decoration-none font-weight-medium">
              {{ item.name }}
            </router-link>
          </template>
          <template #item.date="{ item }">
            {{ formatGigDate(item) }}
          </template>
          <template #item.state="{ item }">
            <v-chip :color="stateColor(item.state)" size="small">{{ item.state }}</v-chip>
          </template>
          <template #item.staffingOpen="{ item }">
            <template v-if="item.staffing">
              <v-chip :color="staffingDisplay(item.staffing).color" size="small" variant="tonal">
                {{ staffingDisplay(item.staffing).label }}
              </v-chip>
              <span v-if="item.staffing.needed" class="text-caption text-medium-emphasis ml-2">
                {{ item.staffing.filled }}/{{ item.staffing.needed }}
              </span>
            </template>
            <span v-else class="text-medium-emphasis">—</span>
          </template>
          <template #item.quoteTotal="{ item }">
            {{ item.quoteTotal != null ? `$${item.quoteTotal.toLocaleString()}` : '—' }}
          </template>
          <template #item.actions="{ item }">
            <v-btn text size="small" :to="`/gigs/${item.id}`" @click.stop>{{ orgStore.isManagerPlus ? 'Edit' : 'View' }}</v-btn>
            <v-menu v-if="orgStore.isManagerPlus">
              <template #activator="{ props }">
                <v-btn icon="mdi-dots-vertical" size="small" variant="text" v-bind="props" @click.stop></v-btn>
              </template>
              <v-list>
                <v-list-item @click="openDuplicate(item.id)">
                  <v-list-item-title>Duplicate</v-list-item-title>
                </v-list-item>
                <v-list-item @click="deleteGig(item.id)">
                  <v-list-item-title>Delete</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
          <template #no-data>
            <div class="text-center py-12">
              <v-icon size="48" class="text-medium-emphasis mb-4">mdi-briefcase-outline</v-icon>
              <p class="text-h6">No gigs yet</p>
              <p class="text-body2 text-medium-emphasis">Create your first gig to get started</p>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Create Gig Dialog -->
    <v-dialog v-model="showCreateDialog" width="500">
      <v-card>
        <v-card-title>Create New Gig</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="handleCreateGig">
            <v-text-field
              v-model="newGig.name"
              label="Gig Name"
              required
              class="mb-4"
            ></v-text-field>

            <div class="d-flex align-center mb-4">
              <v-autocomplete
                v-model="newGig.clientId"
                :items="clientsStore.clients"
                item-title="name"
                item-value="id"
                label="Client"
                :hint="clientsStore.clients.length ? '' : 'No clients yet — add one'"
                persistent-hint
                class="flex-grow-1"
                hide-details="auto"
              ></v-autocomplete>
              <v-btn icon="mdi-plus" size="small" variant="text" class="ml-2" @click="openNewClient" />
            </div>

            <v-textarea
              v-model="newGig.description"
              label="Description"
              rows="3"
              class="mb-4"
            ></v-textarea>

            <v-autocomplete
              v-model="newGig.venueId"
              :items="venuesStore.venues"
              item-title="name"
              item-value="id"
              label="Main venue"
              required
              :rules="[v => !!v || 'A venue is required']"
              :hint="venueHint"
              persistent-hint
              class="mb-4"
            ></v-autocomplete>

            <v-text-field
              v-model="newGig.date"
              label="Date"
              type="date"
              required
              :hint="selectedZone ? `Date and call times are in ${selectedZone}` : ''"
              persistent-hint
              class="mb-4"
            ></v-text-field>

            <div class="text-right">
              <v-btn text @click="showCreateDialog = false">Cancel</v-btn>
              <v-btn color="primary" type="submit" :loading="isCreating">Create</v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <DuplicateGigDialog
      v-if="duplicateGigId"
      v-model="showDuplicateDialog"
      :org-id="route.query.org as string"
      :gig-id="duplicateGigId"
      @duplicated="onGigDuplicated"
    />

    <!-- Quick-add client, opened from the Create Gig dialog -->
    <v-dialog v-model="showNewClientDialog" width="450">
      <v-card>
        <v-card-title>New Client</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="saveNewClient">
            <v-text-field v-model="newClient.name" label="Name" required class="mb-3" />
            <v-text-field v-model="newClient.contactEmail" label="Email" type="email" class="mb-3" />
            <v-text-field v-model="newClient.contactPhone" label="Phone" class="mb-3" />
            <div class="text-right">
              <v-btn text @click="showNewClientDialog = false">Cancel</v-btn>
              <v-btn color="primary" type="submit" :loading="savingNewClient">Save</v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useGigsStore } from '../stores/gigs';
import { useClientsStore } from '../stores/clients';
import { useVenuesStore } from '../stores/venues';
import { useQuotesStore } from '../stores/quotes';
import { startOfDayIn, formatZonedDate, staffingStatus, type GigStaffingSummary, type StaffingStatus } from '@gigdriver/domain';
import { useOrganizationStore } from '../stores/organization';
import DuplicateGigDialog from '../components/DuplicateGigDialog.vue';

const route = useRoute();
const router = useRouter();
const gigsStore = useGigsStore();
const clientsStore = useClientsStore();
const venuesStore = useVenuesStore();
const quotesStore = useQuotesStore();
const orgStore = useOrganizationStore();

const showCreateDialog = ref(false);

const showDuplicateDialog = ref(false);
const duplicateGigId = ref<string | null>(null);
function openDuplicate(gigId: string) {
  duplicateGigId.value = gigId;
  showDuplicateDialog.value = true;
}
function onGigDuplicated(newGigId: string) {
  router.push({ path: `/gigs/${newGigId}`, query: { org: route.query.org as string } });
}
const isCreating = ref(false);
const error = ref<string | null>(null);

const showNewClientDialog = ref(false);
const savingNewClient = ref(false);
const newClient = ref({ name: '', contactEmail: '', contactPhone: '' });

const newGig = ref({
  name: '',
  clientId: '',
  description: '',
  venueId: '',
  date: new Date().toISOString().split('T')[0],
});

// Table paging + sort live in the URL (?page=&perPage=&sort=key:order), so
// Back from a gig lands on the same page of the same sort, and the view can
// be bookmarked/shared. Defaults are omitted to keep the URL clean; replace
// (not push) so paging doesn't pile up history entries.
const DEFAULT_PER_PAGE = 10;
const DEFAULT_SORT: SortItem = { key: 'date', order: 'asc' };

function setQuery(patch: Record<string, string | undefined>) {
  const query = { ...route.query };
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) delete query[k];
    else query[k] = v;
  }
  router.replace({ query });
}

function positiveInt(raw: unknown, fallback: number, allowAll = false): number {
  const n = Number(Array.isArray(raw) ? raw[0] : raw);
  if (!Number.isInteger(n)) return fallback;
  if (allowAll && n === -1) return -1;
  return n >= 1 ? n : fallback;
}

const page = computed({
  get: () => positiveInt(route.query.page, 1),
  set: (v: number) => setQuery({ page: v > 1 ? String(v) : undefined }),
});
const itemsPerPage = computed({
  get: () => positiveInt(route.query.perPage, DEFAULT_PER_PAGE, true),
  // A new page size invalidates the current page number.
  set: (v: number) => setQuery({ perPage: v !== DEFAULT_PER_PAGE ? String(v) : undefined, page: undefined }),
});
const sortBy = computed<SortItem[]>({
  get: () => {
    const raw = route.query.sort;
    const [key, order] = String(Array.isArray(raw) ? raw[0] : (raw ?? '')).split(':');
    if (!SORTABLE_KEYS.has(key) || (order !== 'asc' && order !== 'desc')) return [DEFAULT_SORT];
    return [{ key, order: order as 'asc' | 'desc' }];
  },
  // Clearing the sort (third click on a header) returns to the default.
  set: (v: SortItem[]) => {
    const first = v[0];
    const isDefault = !first || (first.key === DEFAULT_SORT.key && first.order === DEFAULT_SORT.order);
    setQuery({ sort: isDefault ? undefined : `${first.key}:${first.order ?? 'asc'}`, page: undefined });
  },
});

const gigs = computed(() => gigsStore.gigs);
const isLoading = computed(() => gigsStore.isLoading);

interface GigTableHeader {
  title: string;
  key: string;
  sortable?: boolean;
  align?: 'start' | 'end' | 'center';
}
interface SortItem {
  key: string;
  order?: 'asc' | 'desc' | boolean;
}
// Column keys a ?sort= value may name (anything else falls back to the default).
const SORTABLE_KEYS = new Set(['name', 'clientDisplayName', 'date', 'state', 'staffingOpen', 'quoteTotal']);

const headers = computed<GigTableHeader[]>(() => {
  const cols: GigTableHeader[] = [
    { title: 'Name', key: 'name' },
    { title: 'Client', key: 'clientDisplayName' },
    { title: 'Date', key: 'date' },
    { title: 'State', key: 'state' },
    { title: 'Staffing', key: 'staffingOpen' },
  ];
  if (orgStore.isManagerPlus) cols.push({ title: 'Quote', key: 'quoteTotal' });
  cols.push({ title: '', key: 'actions', sortable: false, align: 'end' });
  return cols;
});

// Resolves each row's display-only client name up front so the table's own
// column sort (which reads item[key] directly) can sort by it — the raw gig
// only carries clientId unless clientName was denormalized at write time.
const tableItems = computed(() =>
  gigs.value.map(gig => ({
    ...gig,
    clientDisplayName: gig.clientName ?? clientName(gig.clientId),
    // Sort key for the Staffing column: unfilled slots, so ascending puts
    // fully covered gigs first and descending the ones needing attention.
    // No summary (not computed yet) sorts last-ish as -1.
    staffingOpen: gig.staffing ? gig.staffing.needed - gig.staffing.filled : -1,
    // Computed from the active quote (manager+ only); null until quoted.
    quoteTotal: gig.activeQuoteId ? (quotesStore.orgQuoteTotals[gig.activeQuoteId] ?? null) : null,
  })),
);

function onRowClick(_event: Event, { item }: { item: { id: string } }) {
  router.push(`/gigs/${item.id}`);
}

onMounted(async () => {
  const orgId = route.query.org as string;
  if (orgId) {
    const tasks = [gigsStore.loadOrgGigs(orgId)];
    // Only manager+ can read the clients collection; gig.clientName
    // (denormalized at write time) already covers what others may see.
    if (orgStore.isManagerPlus) {
      tasks.push(clientsStore.loadOrgClients(orgId), venuesStore.loadOrgVenues(orgId));
      quotesStore.watchOrg(orgId);
    }
    await Promise.all(tasks);
    // Fire-and-forget: fills in the Staffing column for older gigs.
    void gigsStore.backfillStaffing(orgId);
  }
});

onUnmounted(() => quotesStore.stopWatchingOrg());

// The chosen main venue's zone — the gig's date (and its calls' times) are
// in it. Legacy venues without a zone fall back to the org's, then the browser's.
const selectedVenue = computed(() => venuesStore.byId(newGig.value.venueId));
const selectedZone = computed(
  () => selectedVenue.value?.timeZone ?? orgStore.currentOrg?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
);
const venueHint = computed(() =>
  venuesStore.venues.length === 0
    ? 'No venues yet — add one on the Venues page first'
    : selectedVenue.value && !selectedVenue.value.timeZone
      ? 'This venue has no time zone yet — using the organization default. Set one on the Venues page.'
      : 'Required. Calls can still use a different venue.',
);

function clientName(clientId: string): string {
  return clientsStore.clients.find(c => c.id === clientId)?.name ?? clientId;
}

function openNewClient() {
  newClient.value = { name: '', contactEmail: '', contactPhone: '' };
  showNewClientDialog.value = true;
}

async function saveNewClient() {
  try {
    savingNewClient.value = true;
    const orgId = route.query.org as string;
    const id = await clientsStore.createClient(orgId, {
      name: newClient.value.name,
      contactEmail: newClient.value.contactEmail || undefined,
      contactPhone: newClient.value.contactPhone || undefined,
    });
    newGig.value.clientId = id;
    showNewClientDialog.value = false;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to add client';
  } finally {
    savingNewClient.value = false;
  }
}

// A gig's calendar day, in its own venue's zone (not the viewer's).
function formatGigDate(gig: { date: Date; timeZone?: string }): string {
  return formatZonedDate(
    new Date(gig.date),
    gig.timeZone ?? orgStore.currentOrg?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    true,
  );
}

const STAFFING_DISPLAY: Record<StaffingStatus, { label: string; color: string }> = {
  NONE: { label: 'No positions', color: 'grey' },
  ALL_FILLED: { label: 'All filled', color: 'success' },
  ALL_OPEN: { label: 'All open', color: 'error' },
  OFFERS_OUT: { label: 'Offers out', color: 'info' },
  TO_SEND: { label: 'Offers to send', color: 'warning' },
  PARTIAL: { label: 'Partly filled', color: 'warning' },
};
function staffingDisplay(s: GigStaffingSummary) {
  return STAFFING_DISPLAY[staffingStatus(s)];
}

function stateColor(state: string): string {
  const colors: Record<string, string> = {
    DRAFT: 'grey',
    QUOTED: 'blue',
    BOOKED: 'green',
    COMPLETED: 'purple',
    INVOICED: 'orange',
    PAID: 'success',
    CANCELLED: 'red',
  };
  return colors[state] || 'grey';
}

async function handleCreateGig() {
  if (!newGig.value.venueId) {
    error.value = 'Choose a main venue for the gig';
    return;
  }
  try {
    isCreating.value = true;
    error.value = null;
    const orgId = route.query.org as string;

    await gigsStore.createGig(orgId, {
      clientId: newGig.value.clientId,
      name: newGig.value.name,
      description: newGig.value.description || '',
      state: 'DRAFT',
      venueId: newGig.value.venueId,
      // Midnight of the chosen day in the VENUE's zone, not the browser's.
      date: startOfDayIn(newGig.value.date, selectedZone.value),
      calls: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    });

    showCreateDialog.value = false;
    newGig.value = {
      name: '',
      clientId: '',
      description: '',
      venueId: '',
      date: new Date().toISOString().split('T')[0],
    };
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to create gig';
  } finally {
    isCreating.value = false;
  }
}

async function deleteGig(gigId: string) {
  if (confirm('Are you sure you want to delete this gig?')) {
    try {
      error.value = null;
      const orgId = route.query.org as string;
      await gigsStore.deleteGig(orgId, gigId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete gig';
    }
  }
}
</script>
